import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {setSelectedPlanDetails} from '../../../store/slice/payment-slice'
import {useDispatch, useSelector} from 'react-redux'
import {useTheme} from '../../../context/ThemeContext'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {getPublicPlans, getAutoApplyCoupon, validateCoupon} from '../../../services/promotions'
import {createSubscription} from '../../../services/subscription'
import {getAllRoles} from '../../../services/user-lookup'
import {createOrganizationMemberInvite} from '../../../services/company'
import {removeAuth} from '../core/AuthHelpers'
import useWebSocket, {ReadyState} from 'react-use-websocket'
import {webSocketOperations} from '../../../constants'
import CustomAlert from '../../../components/CustomAlert'

interface PlanOption {
  id: string
  name: string
  price: number
  billingInterval: 'month' | 'year'
  billingType: string
  bundleMonths?: number
  trialDays?: number
  isActive: boolean
  signupOnly?: boolean
}

const PricingPlan = (): React.JSX.Element => {
  const {planDetails: planUserData} = useSelector((state: any) => state.paymentReducer)
  const [plans, setPlans] = useState<PlanOption[]>([])
  const [selectedPlan, setSelectedPlan] = useState<PlanOption | null>(null)
  const [usersCount, setUsersCount] = useState(
    planUserData?.licenseEmails ? planUserData?.licenseEmails.totalLicense + 1 : 1
  )
  const [plansLoading, setPlansLoading] = useState(true)
  const [plansError, setPlansError] = useState('')
  const [autoApplyCoupon, setAutoApplyCoupon] = useState<any>(null)
  const [manualCouponCode, setManualCouponCode] = useState('')
  const [validatedCoupon, setValidatedCoupon] = useState<any>(null)
  const [couponError, setCouponError] = useState('')
  const [validating, setValidating] = useState(false)
  const [freeTrialLoading, setFreeTrialLoading] = useState(false)
  const [freeTrialError, setFreeTrialError] = useState<any>(null)
  const [freeTrialPopup, setFreeTrialPopup] = useState<any>(null)
  const [roleList, setRoleList] = useState<any[]>([])
  const [socketStatus, setSocketStatus] = useState(ReadyState.CONNECTING)
  const {theme} = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const {sendJsonMessage}: any = useWebSocket(
    `${process.env.REACT_APP_WEBSOCKET_URL}?userid=${planUserData?.userId}&token=${planUserData?.loginToken}`,
    {
      onOpen: () => setSocketStatus(ReadyState.OPEN),
      onClose: () => setSocketStatus(ReadyState.CLOSED),
      onError: () => setSocketStatus(ReadyState.CLOSED),
      shouldReconnect: () => true,
      reconnectAttempts: 10,
      reconnectInterval: 3000,
    }
  )

  // Fetch active plans from API
  useEffect(() => {
    let mounted = true
    setPlansLoading(true)
    getPublicPlans()
      .then((res) => {
        if (!mounted) return
        const data = res.data?.data ?? res.data
        const activePlans = Array.isArray(data) ? data : data?.plans ?? []
        setPlans(activePlans)
        // Auto-select first plan or the one matching previous selection
        if (activePlans.length > 0) {
          const prev = planUserData?.type
          const match = prev ? activePlans.find((p: any) => p.name === prev) : null
          setSelectedPlan(match ?? activePlans[0])
        }
      })
      .catch((err) => {
        if (!mounted) return
        setPlansError(err?.response?.data?.message ?? 'Failed to load plans.')
      })
      .finally(() => mounted && setPlansLoading(false))
    return () => {
      mounted = false
    }
  }, [])

  // Fetch auto-apply coupon whenever selected plan changes
  useEffect(() => {
    let mounted = true
    setAutoApplyCoupon(null)
    if (!selectedPlan?.id) return

    getAutoApplyCoupon(selectedPlan.id)
      .then((res) => {
        if (!mounted) return
        const data = res.data?.data ?? res.data
        if (data?.promotionCode) {
          setAutoApplyCoupon(data)
        }
      })
      .catch(() => {
        // Silently ignore — no auto-apply coupon for this plan
      })
    return () => {
      mounted = false
    }
  }, [selectedPlan?.id])

  const handleUserCount = (event: any) => {
    setUsersCount(event.target.value)
  }

  const countPlan = () => {
    if (!selectedPlan) return '$0.00'
    const price = Number(selectedPlan.price)
    const months = selectedPlan.billingInterval === 'year' ? 12 : (selectedPlan.bundleMonths ?? 1)
    const amount = usersCount * price * months
    return '$' + amount.toFixed(2)
  }

  const formatDiscount = (coupon: any): string => {
    const value = Number(coupon.discountValue)
    if (coupon.discountType === 'percent') return `${value}% off`
    return `$${value.toFixed(2)} off`
  }

  const activeCoupon = autoApplyCoupon?.coupon ?? validatedCoupon

  const getDiscountedTotal = (): string | null => {
    if (!selectedPlan || !activeCoupon) return null
    const price = Number(selectedPlan.price)
    const months = selectedPlan.billingInterval === 'year' ? 12 : (selectedPlan.bundleMonths ?? 1)
    const subtotal = usersCount * price * months
    const discountValue = Number(activeCoupon.value ?? activeCoupon.discountValue)
    let total: number
    if ((activeCoupon.type ?? activeCoupon.discountType) === 'percent') {
      total = subtotal - (subtotal * discountValue) / 100
    } else {
      total = subtotal - discountValue
    }
    return '$' + Math.max(0, total).toFixed(2)
  }

  const handleValidateCoupon = async () => {
    if (!selectedPlan || !manualCouponCode.trim()) return
    setValidating(true)
    setCouponError('')
    setValidatedCoupon(null)
    try {
      const res = await validateCoupon(selectedPlan.id, manualCouponCode.trim())
      const data = res.data?.data ?? res.data
      if (data?.valid && data?.discount) {
        setValidatedCoupon(data.discount)
      } else {
        setCouponError(data?.reason ?? 'This coupon is not available.')
        setValidatedCoupon(null)
      }
    } catch {
      setCouponError('Failed to validate coupon.')
    } finally {
      setValidating(false)
    }
  }

  const clearManualCoupon = () => {
    setManualCouponCode('')
    setValidatedCoupon(null)
    setCouponError('')
  }

  const isFreePlan = selectedPlan ? Number(selectedPlan.price) === 0 : false

  // Roles are only needed when an org-signup user picks a free-tier plan,
  // so we can create license-member invites without the paid checkout flow.
  useEffect(() => {
    if (!isFreePlan || !planUserData?.isOrgSignup) return
    let mounted = true
    getAllRoles()
      .then((res) => {
        if (!mounted) return
        setRoleList(res.data.data ?? [])
      })
      .catch(() => {
        // Silent — invites will surface their own error if role lookup failed
      })
    return () => {
      mounted = false
    }
  }, [isFreePlan, planUserData?.isOrgSignup])

  useEffect(() => {
    if (socketStatus === ReadyState.OPEN && planUserData?.userId) {
      sendJsonMessage({event: 'subscribe', channel: planUserData.userId})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketStatus, planUserData?.userId])

  const freeTrialHandler = async () => {
    if (!selectedPlan) return
    setFreeTrialLoading(true)
    setFreeTrialError(null)
    const role = roleList.find((r: any) => r.name === 'User')?.id
    const data: any = {
      email: planUserData?.email,
      userId: planUserData?.userId,
      name: planUserData?.name,
      type: selectedPlan.name,
      ...(selectedPlan.id ? {planId: selectedPlan.id} : {}),
      totalUser: planUserData?.isOrgSignup ? 1 : Number(usersCount),
      license: planUserData?.licenseEmails && {
        totalLicense: planUserData.licenseEmails.totalLicense,
        organizationId: planUserData.licenseEmails.organizationId,
      },
    }
    try {
      const res = await createSubscription(data)
      const createdLic = res?.data?.data?.license
      if (planUserData?.licenseEmails && createdLic?.length) {
        const memberDetails: any = {
          organizationName: planUserData.licenseEmails.organizationName,
          organizationId: planUserData.licenseEmails.organizationId,
          organizationLicenseId: '',
          email: '',
          roleId: role,
        }
        for (let i = 0; i < createdLic.length; i++) {
          memberDetails.email = planUserData.licenseEmails.licenseEmails[i]
          memberDetails.organizationLicenseId = createdLic[i].id
          await createOrganizationMemberInvite(memberDetails)
        }
      }
      sendJsonMessage({
        event: 'publish',
        channel: planUserData?.userId,
        userId: planUserData?.userId,
        command: 'UPDATE',
        operation: webSocketOperations.isUserVerified,
      })
      setFreeTrialPopup(
        'Thank you for confirming your email. Your registration and subscription are successful — please log in to continue.'
      )
      removeAuth()
      setTimeout(() => {
        navigate('/auth')
      }, 4000)
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Subscription failed. Please try again.'
      setFreeTrialError(msg)
      setFreeTrialLoading(false)
    }
  }

  const buyNowHandler = () => {
    if (!selectedPlan) return
    if (isFreePlan) {
      freeTrialHandler()
      return
    }
    if (manualCouponCode.trim() && !validatedCoupon) {
      setCouponError('Please click Apply to validate your coupon before proceeding.')
      return
    }
    const amountToPay = countPlan()
    if (amountToPay !== '$0.00') {
      const data = {
        totalUser: usersCount,
        amountToPay,
        planType: selectedPlan.name,
        planId: selectedPlan.id,
        couponCode: !autoApplyCoupon?.promotionCode && validatedCoupon ? manualCouponCode : null,
      }
      dispatch(setSelectedPlanDetails({selectedPlanDetails: data}))
      navigate('/auth/payment')
    }
  }

  const dark = theme === 'dark'

  return (
    <div className='w-100'>
      <div className='row mt-15 d-flex justify-content-center'>
        <div className='align-items-center d-flex justify-content-center gap-5'>
          <h1 className={`text-center fs-1 py-3 ${dark ? 'white__text' : ''} mt-7`}>
            Pricing Plan
          </h1>
          <a href={process.env.REACT_APP_B4IGO_LANDING_PAGE} className=''>
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/logos/b4igo-go.png')}
              className='h-100px w-90px my-5 me-15'
            />
          </a>
        </div>
        <div className='col-12'>
          {plansLoading && (
            <div className='text-center py-10'>
              <span className='spinner-border spinner-border-sm me-2' />
              Loading plans...
            </div>
          )}

          {plansError && (
            <div className='alert alert-danger text-center'>{plansError}</div>
          )}

          {!plansLoading && !plansError && plans.length === 0 && (
            <div className='alert alert-info text-center'>No plans available at the moment.</div>
          )}

          {!plansLoading && plans.length > 0 && (
            <>
              <div className='d-flex justify-content-center align-items-center p-3 border rounded '>
                <div className='my-3 d-flex align-items-center justify-content-start gap-12 flex-wrap'>
                  {plans?.map((plan) => (
                    <label className='form-check-label' key={plan.id}>
                      <input
                        type='radio'
                        value={plan.id}
                        className='form-check-input mr-2'
                        checked={selectedPlan?.id === plan.id}
                        onChange={() => setSelectedPlan(plan)}
                      />
                      <span className='text-gray-600 fw-bold' style={{marginLeft: '5px'}}>
                        {plan.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='mb-6 p-3 border'>
                <div className='d-flex justify-content-between align-items-center px-3 mb-2 mt-3'>
                  <h4 className={`${dark ? 'white__text' : ''} d-inline-block`}>
                    Numbers of Users
                  </h4>
                  <input
                    disabled
                    min={1}
                    value={usersCount}
                    onChange={handleUserCount}
                    type='number'
                    className={`form form-control d-inline-block w-25 ms-5 p-2 ${
                      dark ? 'dark__theme__input' : ''
                    }`}
                  />
                </div>
                <hr />
                <div className='d-flex justify-content-between align-items-center px-3 mb-2 mt-3'>
                  <div>
                    <h4 className={dark ? 'white__text' : ''}>
                      {selectedPlan && (
                        <>
                          ${Number(selectedPlan.price).toFixed(2)}
                          {selectedPlan.billingInterval === 'year' ? ' * 12' : ''}/
                          {selectedPlan.billingInterval === 'year' ? 'year' : 'month'} per user
                          {selectedPlan.bundleMonths && selectedPlan.bundleMonths > 1 &&
                            ` for ${selectedPlan.bundleMonths} months`}
                        </>
                      )}
                    </h4>
                  </div>
                  <h4 className={dark ? 'white__text' : ''}>{countPlan()}</h4>
                </div>

                {isFreePlan ? null : autoApplyCoupon?.coupon ? (
                  <>
                    <hr />
                    <div className='d-flex justify-content-between align-items-center px-3 mb-2'>
                      <div className='badge badge-light-success fs-7'>
                        {formatDiscount(autoApplyCoupon.coupon)} automatically applied
                        <span className='text-muted ms-2 fs-9'>({autoApplyCoupon.coupon.code})</span>
                      </div>
                      <span className='text-success fw-bold'>
                        -{autoApplyCoupon.coupon.discountType === 'percent'
                          ? `${Number(autoApplyCoupon.coupon.discountValue)}%`
                          : `$${Number(autoApplyCoupon.coupon.discountValue).toFixed(2)}`}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <hr />
                    {validatedCoupon ? (
                      <div className='d-flex justify-content-between align-items-center px-3 mb-2'>
                        <div className='badge badge-light-success fs-7'>
                          {Number(validatedCoupon.value)}
                          {validatedCoupon.type === 'percent' ? '% off' : ` ${(validatedCoupon.currency ?? 'USD').toUpperCase()} off`}
                          <span className='text-muted ms-2 fs-9'>({validatedCoupon.code})</span>
                        </div>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger py-1 px-3'
                          onClick={clearManualCoupon}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className='px-3 mb-2'>
                        <div className='d-flex align-items-center gap-2'>
                          <input
                            type='text'
                            className={`form-control form-control-sm ${dark ? 'dark__theme__input' : ''}`}
                            placeholder='Coupon code'
                            value={manualCouponCode}
                            onChange={(e) => {
                              setManualCouponCode(e.target.value.toUpperCase())
                              setCouponError('')
                            }}
                            maxLength={40}
                          />
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary text-nowrap'
                            onClick={handleValidateCoupon}
                            disabled={!manualCouponCode.trim() || validating}
                          >
                            {validating ? 'Checking...' : 'Apply'}
                          </button>
                        </div>
                        {couponError && (
                          <span className='text-danger fs-9 mt-1 d-block'>{couponError}</span>
                        )}
                      </div>
                    )}
                  </>
                )}

                <hr />
                <div className='d-flex justify-content-between align-items-center px-3 my-2'>
                  <h4 className={dark ? 'white__text' : ''}> Total</h4>
                  <h4 className={dark ? 'white__text' : ''}>
                    {getDiscountedTotal() ? (
                      <>
                        <span className='text-decoration-line-through text-muted me-2 fs-6'>{countPlan()}</span>
                        {getDiscountedTotal()}
                      </>
                    ) : (
                      countPlan()
                    )}
                  </h4>
                </div>

                <button
                  type='button'
                  className='btn btn-primary w-100 my-1'
                  onClick={buyNowHandler}
                  disabled={!selectedPlan || freeTrialLoading}
                >
                  {isFreePlan
                    ? freeTrialLoading
                      ? 'Processing...'
                      : 'Continue with this tier'
                    : 'Buy Now'}
                </button>

                {isFreePlan && (
                  <div className='mt-3'>
                    <CustomAlert
                      variant={'danger'}
                      className={'my-2'}
                      alertMessage={freeTrialError}
                      setAlertMessage={setFreeTrialError}
                      time={5000}
                    />
                    <CustomAlert
                      variant={'primary'}
                      className={'my-2'}
                      alertMessage={freeTrialPopup}
                      setAlertMessage={setFreeTrialPopup}
                      time={5000}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PricingPlan
